import { Fragment, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth, useClerk } from '@clerk/clerk-react'
import {
  Wallet,
  Receipt,
  PieChart,
  Target,
  CalendarClock,
  Users,
  LineChart,
  ShieldCheck,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  LogOut,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LogoMark } from '@/components/Logo'
import { ROUTES } from '@/routes/routes'
import { useInView } from '@/hooks/useInView'
import { useCountUp } from '@/hooks/useCountUp'
import { HeroParticleField } from '@/components/HeroParticleField'
import { ScreenshotCarousel } from '@/components/ScreenshotCarousel'
import { fetchPlans, type BillingPlanCode } from '@/api/billing'
import { discountPercent } from '@/lib/planPricing'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { signOutAndReset } from '@/lib/signOut'
import { cn } from '@/lib/utils'

// Where the page's primary calls-to-action point. Signed-out visitors are pushed toward
// sign-up; a visitor who's already signed in gets sent to their dashboard instead — the
// marketing CTAs would otherwise ask them to "start a free trial" they already have.
interface PrimaryCta {
  to: string
  /** Full label for the hero / closing CTA buttons. */
  long: string
  /** Short label for the pricing-card buttons. */
  short: string
}

const PLAN_ORDER: BillingPlanCode[] = ['MONTHLY', 'ANNUAL']
const PLAN_NOTE: Record<BillingPlanCode, string> = {
  MONTHLY: 'Billed monthly after the trial. Cancel anytime.',
  ANNUAL: 'Billed yearly after the trial — the best value.',
}

function PricingSection({ cta }: { cta: PrimaryCta }) {
  const { data: plans } = useQuery({ queryKey: ['billing-plans'], queryFn: fetchPlans })
  const ordered = PLAN_ORDER.map((code) => plans?.find((p) => p.code === code)).filter(
    (p): p is NonNullable<typeof p> => Boolean(p)
  )
  const maxPctOff = Math.max(
    0,
    ...ordered.map((p) => discountPercent(p.amountPaise, p.listAmountPaise))
  )

  return (
    <section
      id="pricing"
      className="mx-auto max-w-[1180px] px-4 pb-4 pt-8 sm:px-6 sm:pb-10 sm:pt-[120px]"
    >
      <div className="mb-4 max-w-[640px] sm:mb-16">
        <span className="mb-2 inline-block text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--fl-primary)] sm:mb-[18px] sm:text-[13px]">
          Pricing
        </span>
        <h2
          className="text-[21px] font-bold leading-[1.15] tracking-tight text-[var(--fl-heading)] sm:text-[clamp(34px,4.5vw,50px)] sm:leading-[1.08]"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Free for 30 days. No card to start.
        </h2>
        {maxPctOff > 0 && (
          <p className="mt-1.5 text-[12.5px] font-semibold text-[var(--fl-primary)] sm:mt-4 sm:text-[15px]">
            Launch offer — up to {maxPctOff}% off the regular price.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-5">
        {ordered.map((plan) => {
          const price = `₹${(plan.amountPaise / 100).toLocaleString('en-IN')}`
          const listPrice = `₹${(plan.listAmountPaise / 100).toLocaleString('en-IN')}`
          const suffix = plan.interval === 'MONTH' ? '/mo' : '/yr'
          const off = discountPercent(plan.amountPaise, plan.listAmountPaise)
          return (
            <div
              key={plan.code}
              className="flex flex-col rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-3 sm:rounded-[20px] sm:p-7"
            >
              <div className="flex items-baseline justify-between gap-1">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.06em] text-[var(--fl-heading)] sm:text-sm sm:tracking-[0.08em]">
                  {plan.code.toLowerCase()}
                </h3>
                {off > 0 && (
                  <span className="whitespace-nowrap rounded-full bg-[var(--fl-primary-subtle)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--fl-primary-text)] sm:px-2.5 sm:text-[12px]">
                    {off}% off
                  </span>
                )}
              </div>
              <div className="mt-2 sm:mt-3">
                {off > 0 && (
                  <p className="text-xs font-medium text-[var(--fl-text-3)] line-through sm:text-sm">
                    {listPrice}
                    {suffix}
                  </p>
                )}
                <p
                  className="font-serif text-[26px] font-bold text-[var(--fl-heading)] sm:text-4xl"
                  style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
                >
                  {price}
                  <span className="text-[13px] font-medium text-[var(--fl-text-3)] sm:text-lg">
                    {suffix}
                  </span>
                </p>
              </div>
              <p className="mt-1.5 flex-1 text-[11.5px] leading-snug text-[var(--fl-text-2)] sm:mt-3 sm:text-[14.5px] sm:leading-relaxed">
                {PLAN_NOTE[plan.code]}
              </p>
              <Button
                asChild
                className="mt-3 h-auto rounded-full bg-[var(--fl-primary)] px-3 py-2 text-[12px] font-bold sm:mt-6 sm:px-6 sm:py-3 sm:text-sm text-[var(--fl-primary-fg)] hover:bg-[var(--fl-primary-hover)]"
              >
                <Link to={cta.to}>{cta.short}</Link>
              </Button>
            </div>
          )
        })}
      </div>
    </section>
  )
}

interface Feature {
  icon: LucideIcon
  title: string
  description: string
}

const FEATURES: Feature[] = [
  {
    icon: Wallet,
    title: 'Every account, one place',
    description:
      'Bank, cash, credit cards, subscriptions, loans, property, market-linked investments — track them all in your own currency, without linking a single account.',
  },
  {
    icon: Receipt,
    title: 'Log in seconds',
    description:
      'Income, expenses, transfers, and refunds — categorised and searchable the moment you enter them.',
  },
  {
    icon: PieChart,
    title: 'Budgets that hold',
    description:
      'Set a limit per category, watch it in real time, know before you overspend — not after.',
  },
  {
    icon: Target,
    title: 'Goals with real progress',
    description:
      'Dedicate an account to a goal or earmark funds inside one — either way, progress tracks itself.',
  },
  {
    icon: CalendarClock,
    title: 'Bills that mark themselves',
    description:
      'Recurring bills, subscriptions, and EMIs log their own transaction the moment you mark them paid.',
  },
  {
    icon: TrendingUp,
    title: 'Loans, property & investments',
    description:
      'EMI schedules for every loan, and real valuation tracking for property and market-linked investments — not just a balance that moves with transactions.',
  },
  {
    icon: Users,
    title: 'Shared with your people',
    description: "Invite your family in, or attribute spend to someone who'll never open the app.",
  },
  {
    icon: LineChart,
    title: 'Patterns, not just totals',
    description:
      'A calendar and analytics view that shows how you actually spend, month over month.',
  },
  {
    icon: ShieldCheck,
    title: 'Locked down properly',
    description:
      'Encrypted connections, managed sign-in, and no bank logins to store or leak — your ledger stays yours.',
  },
]

const STATS = [
  {
    target: 5,
    suffix: '+',
    label: 'Account types tracked side by side — bank, cash, cards, loans, investments',
  },
  { target: 100, suffix: '%', label: 'Manual entry, full control — no bank linking, ever' },
  // Matches the backend cap (InviteController / MemberController: 10 members per ledger,
  // signed-in members + name-only tracked people combined).
  {
    target: 10,
    suffix: '',
    label: 'People you can track spend for in one ledger — logged in or not',
  },
]

const STEPS = [
  {
    title: 'Add your accounts',
    description: 'Bank, cash, cards, loans — however your money is actually spread out.',
  },
  {
    title: 'Log as you go',
    description:
      'A few seconds per entry. Recurring bills and EMIs log themselves after the first setup.',
  },
  {
    title: 'See it clearly',
    description: 'Budgets, goals, and patterns update themselves — no spreadsheet required.',
  },
]

function FeatureCard({ feature, index }: { feature: Feature; index: number }) {
  const [ref, inView] = useInView({ threshold: 0.15, rootMargin: '0px 0px -40px 0px' })
  const Icon = feature.icon
  return (
    <div
      ref={ref}
      className={cn(
        'w-[78%] shrink-0 snap-start rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] p-4 transition-all sm:w-auto sm:rounded-none sm:border-0 sm:p-7 sm:pb-8 duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[var(--fl-surface-hover)]',
        inView ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
      )}
      style={{ transitionDelay: `${(index % 3) * 60}ms` }}
    >
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-[10px] bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)] sm:mb-5 sm:h-11 sm:w-11 sm:rounded-xl">
        <Icon size={20} />
      </div>
      <h3 className="mb-1.5 text-[15px] font-bold text-[var(--fl-heading)] sm:mb-2 sm:text-[17px]">
        {feature.title}
      </h3>
      <p className="text-[13px] leading-relaxed text-[var(--fl-text-2)] sm:text-sm">
        {feature.description}
      </p>
    </div>
  )
}

// A phone swipes through the features one card at a time; wider screens see them all as a grid.
function FeatureCarousel() {
  const [active, setActive] = useState(0)

  function onScroll(e: React.UIEvent<HTMLDivElement>) {
    const card = e.currentTarget.firstElementChild as HTMLElement | null
    if (!card) return
    // 10px is the gap-2.5 between cards.
    const index = Math.round(e.currentTarget.scrollLeft / (card.offsetWidth + 10))
    setActive(Math.min(FEATURES.length - 1, Math.max(0, index)))
  }

  return (
    <>
      <div
        onScroll={onScroll}
        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-2.5 overflow-x-auto px-4 pb-1.5 [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-px sm:overflow-hidden sm:rounded-[22px] sm:border sm:border-[var(--fl-border)] sm:bg-[var(--fl-border)] sm:p-0 lg:grid-cols-3"
      >
        {FEATURES.map((feature, index) => (
          <FeatureCard key={feature.title} feature={feature} index={index} />
        ))}
      </div>
      <div aria-hidden="true" className="mt-2 flex justify-center gap-1.5 sm:hidden">
        {FEATURES.map((feature, index) => (
          <span
            key={feature.title}
            className={cn(
              'h-1.5 rounded-full transition-all',
              index === active ? 'w-4 bg-[var(--fl-primary)]' : 'w-1.5 bg-[var(--fl-border)]'
            )}
          />
        ))}
      </div>
    </>
  )
}

function CountStat({ target, suffix }: { target: number; suffix: string }) {
  const value = useCountUp(target, { durationMs: 1100 })
  return (
    <div
      className="font-serif text-[clamp(36px,4.5vw,56px)] font-bold tabular-nums tracking-tight text-[var(--fl-deep-fg)]"
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {Math.round(value)}
      <span className="text-[#8fd6ae]">{suffix}</span>
    </div>
  )
}

function StatBand() {
  const [ref, inView] = useInView({ threshold: 0.4 })
  return (
    <div
      ref={ref}
      className="mt-8 bg-[linear-gradient(175deg,var(--fl-deep),var(--fl-deep-2))] px-5 py-7 sm:mt-[140px] sm:px-6 sm:py-28"
    >
      <div className="mx-auto max-w-[1000px] text-center">
        <span className="mb-2 inline-block text-[11px] font-bold uppercase tracking-[0.08em] text-[#8fd6ae] sm:mb-4 sm:text-[13px]">
          Why a ledger, not an app
        </span>
        <h2
          className="mx-auto max-w-[760px] text-2xl font-bold leading-[1.2] text-[var(--fl-deep-fg)] sm:text-[clamp(32px,4.5vw,46px)] sm:leading-[1.15]"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Money doesn&rsquo;t need syncing. It needs attention.{' '}
          {/* On a phone the rest reads as a smaller line under the headline. */}
          <span className="max-sm:mt-2.5 max-sm:block max-sm:font-sans max-sm:text-[13.5px] max-sm:font-normal max-sm:leading-normal max-sm:text-[var(--fl-deep-fg-2)]">
            Auto-sync quietly does the first and skips the second — LogMySpends asks you to log it
            yourself, on purpose, every time.
          </span>
        </h2>
        <div className="mt-20 hidden grid-cols-1 gap-10 border-t border-white/[0.14] pt-14 sm:grid sm:grid-cols-3 sm:gap-6">
          {STATS.map((stat) => (
            <div key={stat.label} className="text-left">
              {inView ? (
                <CountStat target={stat.target} suffix={stat.suffix} />
              ) : (
                <div className="font-serif text-[clamp(36px,4.5vw,56px)] font-bold tabular-nums text-[var(--fl-deep-fg)]">
                  0
                </div>
              )}
              <p className="mt-2.5 max-w-[240px] text-[14.5px] leading-relaxed text-[var(--fl-deep-fg-2)]">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function Step({
  index,
  title,
  description,
}: {
  index: number
  title: string
  description: string
}) {
  const [ref, inView] = useInView({ threshold: 0.3 })
  return (
    <div
      ref={ref}
      className={cn(
        'flex min-w-0 flex-1 items-center gap-1.5 transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] sm:block',
        inView ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
      )}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[var(--fl-primary-subtle)] font-serif text-[11px] font-bold text-[var(--fl-primary)] sm:mb-4 sm:block sm:h-auto sm:w-auto sm:rounded-none sm:bg-transparent sm:text-[15px]">
        <span className="sm:hidden">{index + 1}</span>
        <span className="hidden sm:inline">{String(index + 1).padStart(2, '0')}</span>
      </div>
      <h3 className="text-[12.5px] font-semibold leading-tight text-[var(--fl-heading)] sm:mb-2.5 sm:text-xl sm:font-bold">
        {title}
      </h3>
      <p className="hidden text-[14.5px] leading-relaxed text-[var(--fl-text-2)] sm:block">
        {description}
      </p>
    </div>
  )
}

/** Signed-in nav control: the account label as a trigger, with Dashboard / Log out in a
 *  dropdown below it. Closes on outside click or after picking an item. */
function AccountNavMenu({
  label,
  shortLabel,
  onSignOut,
}: {
  label: string
  /** What fits on a phone — the first name. */
  shortLabel: string
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        className="flex max-w-[140px] items-center gap-1 rounded-full border border-[var(--fl-border)] px-3 py-1.5 text-[13px] font-semibold text-[var(--fl-text-2)] transition-colors hover:border-[var(--fl-primary)] hover:text-[var(--fl-primary)] sm:max-w-[240px] sm:gap-1.5 sm:px-[18px] sm:py-2.5 sm:text-sm"
      >
        <span className="truncate sm:hidden">{shortLabel}</span>
        <span className="hidden truncate sm:inline">{label}</span>
        <ChevronDown size={15} aria-hidden="true" className="shrink-0" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-48 rounded-xl border border-[var(--fl-border)] bg-[var(--fl-surface)] py-1.5 shadow-[0_4px_20px_rgba(0,0,0,0.1)]"
        >
          <Link
            to={ROUTES.APP.DASHBOARD}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
          >
            Dashboard
          </Link>
          <div className="my-1.5 border-t border-[var(--fl-border)]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              onSignOut()
            }}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
          >
            <LogOut size={14} aria-hidden="true" className="shrink-0" /> Log out
          </button>
        </div>
      )}
    </div>
  )
}

export default function HomePage() {
  const { isSignedIn } = useAuth()
  const clerk = useClerk()
  const { data: currentUser } = useCurrentUser()
  const signedIn = !!isSignedIn

  // Marketing-page sign-out: Clerk session + local household/query cache, back to the home page.
  const handleSignOut = () => signOutAndReset(clerk, '/')

  const accountLabel =
    currentUser?.displayName?.trim() || currentUser?.household?.name?.trim() || 'My dashboard'
  const accountShortLabel = currentUser?.displayName?.trim()
    ? currentUser.displayName.trim().split(/\s+/)[0]
    : accountLabel

  const cta: PrimaryCta = signedIn
    ? { to: ROUTES.APP.DASHBOARD, long: 'Go to your dashboard', short: 'Go to your dashboard' }
    : {
        to: ROUTES.AUTH.SIGN_UP,
        long: 'Start your 30-day free trial',
        short: 'Start free trial',
      }

  return (
    <div className="fl-force-light min-h-screen overflow-x-clip pb-20 sm:pb-0">
      <nav className="mx-auto flex max-w-[1180px] items-center justify-between gap-3 px-4 pt-4 sm:px-6 sm:pt-7">
        <div className="flex items-center gap-2.5">
          <LogoMark size="sm" />
          <span
            className="text-lg font-bold tracking-tight text-[var(--fl-heading)] sm:text-xl"
            style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
          >
            LogMySpends
          </span>
        </div>
        <div className="flex items-center gap-3 sm:gap-6">
          <a
            href="#pricing"
            className="text-[13px] font-semibold sm:text-sm text-[var(--fl-text-2)] transition-colors hover:text-[var(--fl-primary)]"
          >
            Pricing
          </a>
          {signedIn ? (
            <AccountNavMenu
              label={accountLabel}
              shortLabel={accountShortLabel}
              onSignOut={handleSignOut}
            />
          ) : (
            <Link
              to={ROUTES.AUTH.SIGN_IN}
              className="rounded-full border border-[var(--fl-border)] px-3 py-1.5 text-[13px] font-semibold sm:px-[18px] sm:py-2.5 sm:text-sm text-[var(--fl-text-2)] transition-colors hover:border-[var(--fl-primary)] hover:text-[var(--fl-primary)]"
            >
              Sign in
            </Link>
          )}
        </div>
      </nav>

      <header className="relative mx-auto max-w-[900px] px-5 pb-6 pt-10 text-center sm:px-6 sm:pb-16 sm:pt-24">
        <HeroParticleField />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-[-120px] h-[560px] w-[900px] -translate-x-1/2 motion-safe:animate-glow-breathe"
          style={{
            background:
              'radial-gradient(closest-side, rgba(45,122,95,0.16), rgba(45,122,95,0.05) 55%, transparent 75%)',
            filter: 'blur(10px)',
          }}
        />

        <div className="relative">
          <div className="mx-auto mb-5 h-[3px] w-12 origin-center sm:mb-8 sm:w-16 rounded-full bg-[var(--fl-primary)] motion-safe:animate-grow-line" />

          <h1 className="font-serif text-[44px] font-extrabold leading-[0.98] tracking-tight text-[var(--fl-heading)] motion-safe:animate-hero-fade-up [animation-delay:150ms] sm:text-7xl">
            Know your <br />
            <span className="text-[var(--fl-primary)]">money.</span>
          </h1>

          <p className="mx-auto mt-4 max-w-md text-[15px] text-[var(--fl-text-2)] sm:mt-7 sm:text-lg motion-safe:animate-hero-fade-up [animation-delay:300ms]">
            Every transaction, every account, every bill — one ledger that tells you exactly where
            you stand. No bank linking, no guesswork.
          </p>

          {/* A phone has the bottom bar for this. */}
          <div className="mt-10 hidden flex-wrap items-center justify-center gap-5 sm:flex motion-safe:animate-hero-fade-up [animation-delay:450ms]">
            <Button
              asChild
              className="relative h-auto overflow-hidden rounded-full bg-[var(--fl-primary)] px-10 py-4 text-base font-bold text-[var(--fl-primary-fg)] shadow-[0_10px_28px_-8px_var(--fl-primary)] transition-transform duration-150 ease-out hover:scale-[1.045] hover:bg-[var(--fl-primary-hover)]"
            >
              <Link to={cta.to}>
                <span className="relative z-10">
                  {cta.long} <span className="inline-block motion-safe:animate-nudge">→</span>
                </span>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-0 -left-[60%] w-[40%] -skew-x-[20deg] bg-gradient-to-r from-transparent via-white/40 to-transparent motion-safe:animate-shimmer-sweep"
                />
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1180px] px-4 sm:px-6">
        <ScreenshotCarousel />
      </div>

      <section className="mx-auto max-w-[1180px] px-4 pb-4 pt-8 sm:px-6 sm:pb-10 sm:pt-[120px]">
        <div className="mb-4 max-w-[640px] sm:mb-16">
          <span className="mb-2 inline-block text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--fl-primary)] sm:mb-[18px] sm:text-[13px]">
            Everything, one ledger
          </span>
          <h2
            className="text-[21px] font-bold leading-[1.15] tracking-tight text-[var(--fl-heading)] sm:text-[clamp(34px,4.5vw,50px)] sm:leading-[1.08]"
            style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
          >
            Built to answer one question — where did the money go?
          </h2>
        </div>
        <FeatureCarousel />
      </section>

      <StatBand />

      <section className="mx-auto max-w-[1180px] px-4 pb-4 pt-8 sm:px-6 sm:pb-10 sm:pt-[120px]">
        <div className="mb-4 max-w-[640px] sm:mb-16">
          <span className="mb-2 inline-block text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--fl-primary)] sm:mb-[18px] sm:text-[13px]">
            How it works
          </span>
          <h2
            className="text-[21px] font-bold leading-[1.15] tracking-tight text-[var(--fl-heading)] sm:text-[clamp(34px,4.5vw,50px)] sm:leading-[1.08]"
            style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
          >
            Three steps from sign-up to seeing everything clearly.
          </h2>
        </div>
        <div className="flex items-center gap-1.5 rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-2.5 py-3 sm:grid sm:grid-cols-3 sm:gap-12 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0">
          {STEPS.map((step, index) => (
            <Fragment key={step.title}>
              {index > 0 && (
                <ChevronRight
                  size={14}
                  aria-hidden="true"
                  className="shrink-0 text-[var(--fl-text-3)] sm:hidden"
                />
              )}
              <Step index={index} title={step.title} description={step.description} />
            </Fragment>
          ))}
        </div>
      </section>

      <PricingSection cta={cta} />

      {/* A phone has the bottom bar instead. */}
      <section className="mx-auto hidden max-w-[720px] px-6 pb-24 pt-[140px] text-center sm:block">
        <h2
          className="text-[clamp(34px,5vw,54px)] font-bold leading-[1.1] text-[var(--fl-heading)]"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          Start your ledger today.
        </h2>
        <p className="mb-10 mt-5 text-lg text-[var(--fl-text-2)]">
          Free to use. No bank linking. No card required.
        </p>
        <Button
          asChild
          className="relative h-auto overflow-hidden rounded-full bg-[var(--fl-primary)] px-10 py-4 text-base font-bold text-[var(--fl-primary-fg)] shadow-[0_10px_28px_-8px_var(--fl-primary)] transition-transform duration-150 ease-out hover:scale-[1.045] hover:bg-[var(--fl-primary-hover)]"
        >
          <Link to={cta.to}>
            {cta.long} <span aria-hidden="true">→</span>
          </Link>
        </Button>
      </section>

      <footer className="mt-6 border-t border-[var(--fl-border)] px-4 py-6 sm:mt-0 sm:px-6 sm:py-8">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-3 text-[13px] text-[var(--fl-text-3)]">
          <span>LogMySpends</span>
          <div className="flex items-center gap-4">
            <Link
              to={ROUTES.LEGAL.PRIVACY}
              className="hover:text-[var(--fl-text-2)] hover:underline"
            >
              Privacy
            </Link>
            <Link to={ROUTES.LEGAL.TERMS} className="hover:text-[var(--fl-text-2)] hover:underline">
              Terms
            </Link>
          </div>
          <p>
            {signedIn ? (
              <Link
                to={ROUTES.APP.DASHBOARD}
                className="font-medium text-[var(--fl-primary)] hover:underline"
              >
                Go to your dashboard
              </Link>
            ) : (
              <>
                Already have an account?{' '}
                <Link
                  to={ROUTES.AUTH.SIGN_IN}
                  className="font-medium text-[var(--fl-primary)] hover:underline"
                >
                  Sign in
                </Link>
              </>
            )}
          </p>
        </div>
      </footer>

      <div
        data-testid="mobile-cta-bar"
        className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-3 border-t border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 pt-2.5 pb-[max(12px,env(safe-area-inset-bottom))] shadow-[0_-8px_24px_rgba(0,0,0,0.06)] sm:hidden"
      >
        {!signedIn && (
          <p className="text-[12.5px] leading-tight text-[var(--fl-text-2)]">
            30 days free
            <br />
            <span className="font-semibold text-[var(--fl-heading)]">No card to start</span>
          </p>
        )}
        <Button
          asChild
          className={cn(
            'h-auto rounded-full bg-[var(--fl-primary)] px-5 py-2.5 text-sm font-bold text-[var(--fl-primary-fg)] hover:bg-[var(--fl-primary-hover)]',
            signedIn && 'flex-1'
          )}
        >
          <Link to={cta.to}>
            {cta.short} <span aria-hidden="true">→</span>
          </Link>
        </Button>
      </div>
    </div>
  )
}
